import { useMemo, useState } from 'react'
import {
  MilesDrivenOnCurrentTires,
  AirConditioning,
  Answer,
  VehicleType,
} from '../../../api-client/enums'
import Checkmark from '../../../components/svg/checkmark'
import Fail from '../../../components/svg/fail'
import Engine from '../../../components/svg/engine'
import ABS from '../../../components/svg/abs'
import Airbag from '../../../components/svg/airbag'
import AllWD from '../../../components/svg/allWD'
import Oil from '../../../components/svg/oil'
import Battery from '../../../components/svg/battery'
import TPMS from '../../../components/svg/tpms'
import Lamp from '../../../components/svg/lamp'
import EngineCheck from '../../../components/svg/engine-check'
import useVehiclePageData from '../use-vehicle-page-data'
import Exterior from './exterior'
import Interior from './interior'
import Mechanical from './mechanical'
import Tires from './tires'
import Tabs from './tabs'

export const issueTypes = {
  serviceLights: 1,
  image: 2,
  text: 3,
}

const checkmarks = {
  noIssue: <Checkmark width='20' height='20' fill='var(--success-color)' />,
  issue: <Fail width='20' height='18' />,
}

const noIssueCondition = [
  {
    icon: checkmarks.noIssue,
    label: 'No issues reported',
    type: issueTypes.text,
  },
]

function getIssueCount(properties, vehicleData) {
  let count = 0

  properties.forEach((property) => {
    if (vehicleData[property] != null) {
      switch (property) {
        case 'milesDrivenOnCurrentTires':
          if (vehicleData[property] === MilesDrivenOnCurrentTires.Over25k) count++
          break
        case 'airConditioning':
          if (vehicleData[property] !== AirConditioning.BlowsCold) count++
          break
        case 'serviceLightsOn':
          if (vehicleData[property] === Answer.Yes) count++
          break
        case 'hasAdditionalComments':
          if (vehicleData[property] === Answer.Yes) count++
          break
        case 'transmissionHasIssues':
          if (vehicleData[property] === Answer.Yes) count++
          break
        case 'engineHasIssues':
          if (vehicleData[property] === Answer.Yes) count++
          break
        default:
          if (vehicleData[property].answer === Answer.Yes) count++
          break
      }
    }
  })

  return count
}

function generateIssueCondition(issueObject) {
  if (issueObject?.answer === Answer.Yes) {
    return issueObject.issueDetails.map((issue) => ({
      image: issue.photo?.url,
      label: issue.description,
      type: issue.photo?.url ? issueTypes.image : issueTypes.text,
      icon: checkmarks.issue,
    }))
  }

  return noIssueCondition
}

function generateExteriorIssues(vehicleData) {
  return [
    {
      description: 'Issues with the driver side',
      conditions: generateIssueCondition(vehicleData.driverSideIssue),
    },
    {
      description: 'Issues with the passenger side',
      conditions: generateIssueCondition(vehicleData.passengerSideIssue),
    },
    {
      description: 'Issues with the front of the vehicle',
      conditions: generateIssueCondition(vehicleData.frontSideIssue),
    },
    {
      description: 'Issues with the rear of the vehicle',
      conditions: generateIssueCondition(vehicleData.rearSideIssue),
    },
  ]
}

function generateInteriorIssues(vehicleData) {
  return [
    {
      type: issueTypes.image,
      description: 'Issues with the carpet or upholstery pet hair',
      conditions: generateIssueCondition(vehicleData.carpetOrUpholsteryPetHairIssue),
    },
    {
      type: issueTypes.image,
      description: 'Issues with the carpet or upholstery',
      conditions: generateIssueCondition(vehicleData.carpetOrUpholsteryIssue),
    },
    {
      type: issueTypes.image,
      description: 'Issues with the trim or dashboard',
      conditions: generateIssueCondition(vehicleData.trimOrDashboardIssue),
    },
    {
      type: issueTypes.text,
      description: 'Issues with pet odors',
      conditions: generateIssueCondition(vehicleData.petOdorIssue),
    },
    {
      type: issueTypes.text,
      description: 'Issues with odors',
      conditions: generateIssueCondition(vehicleData.odorIssue),
    },
  ]
}

function generateMechanicalIssues(vehicleData) {
  const tiresMilageOptions = [
    { label: 'Unknown', value: MilesDrivenOnCurrentTires.None },
    { label: 'Under 10,000 mi', value: MilesDrivenOnCurrentTires.Under10k },
    { label: '10k to 20K mi', value: MilesDrivenOnCurrentTires.From10kTo20k },
    { label: 'Over 20,000 mi', value: MilesDrivenOnCurrentTires.Over25k },
  ]
  const airConditioningOptions = [
    { label: 'Unknown', value: AirConditioning.None },
    { label: 'Blows cold', value: AirConditioning.BlowsCold },
    { label: 'Not working', value: AirConditioning.NotWorking },
    { label: 'No AC', value: AirConditioning.NoAc },
  ]
  const conditionTypes = {
    1: { icon: <Engine />, label: 'Engine' },
    2: { icon: <ABS />, label: 'ABS' },
    3: { icon: <EngineCheck />, label: 'Service' },
    4: { icon: <Airbag />, label: 'Airbag' },
    5: { icon: <AllWD />, label: '4x4' },
    6: { icon: <Oil />, label: 'Fluid' },
    7: { icon: <Battery />, label: 'Battery' },
    8: { icon: <TPMS />, label: 'TPMS' },
    9: { icon: <Lamp />, label: 'Other' },
  }

  return [
    {
      description: 'Issues with the vehicle’s engine.',
      hasLookedAtByTechnician: vehicleData.engineIssuesLookedAtByTechnician === Answer.Yes,
      repairEstimate: vehicleData.estimateOfEngineIssuesRepair,
      conditions:
        vehicleData.engineHasIssues === Answer.Yes
          ? [
              {
                icon: checkmarks.issue,
                label: 'Engine Has Issues.',
                type: issueTypes.text,
                issueDescription: vehicleData.engineIssuesDescription,
              },
            ]
          : noIssueCondition,
    },
    {
      description: 'Issues with the vehicle’s transmission.',
      hasLookedAtByTechnician: vehicleData.transmissionIssuesLookedAtByTechnician === Answer.Yes,
      repairEstimate: vehicleData.estimateOfTransmissionIssuesRepair,
      conditions:
        vehicleData.transmissionHasIssues === Answer.Yes
          ? [
              {
                icon: checkmarks.issue,
                label: 'Transmission Has Issues.',
                type: issueTypes.text,
                issueDescription: vehicleData.transmissionIssuesDescription,
              },
            ]
          : noIssueCondition,
    },
    {
      description: 'Vehicle service lights on.',
      hasLookedAtByTechnician: vehicleData.serviceLightIssueLookedAtByTechnician === Answer.Yes,
      repairEstimate: vehicleData.estimateOfServiceLightIssueRepair,
      conditions:
        vehicleData.serviceLightsOn === Answer.Yes
          ? vehicleData.conditionTypes.map((type) => ({
              icon: conditionTypes[type].icon,
              label: conditionTypes[type].label,
              type: issueTypes.serviceLights,
            }))
          : noIssueCondition,
    },
    {
      description: 'Tire mileage',
      conditions: [
        {
          icon:
            vehicleData.milesDrivenOnCurrentTires === MilesDrivenOnCurrentTires.Over25k
              ? checkmarks.issue
              : checkmarks.noIssue,
          label: tiresMilageOptions.find(
            ({ value }) => value === vehicleData.milesDrivenOnCurrentTires,
          ).label,
          type: issueTypes.text,
        },
      ],
    },
    {
      description: 'Air conditioning',
      conditions: [
        {
          icon:
            vehicleData.airConditioning === AirConditioning.BlowsCold
              ? checkmarks.noIssue
              : checkmarks.issue,
          label: airConditioningOptions.find(({ value }) => value === vehicleData.airConditioning)
            .label,
          type: issueTypes.text,
        },
      ],
    },
    {
      description: 'Additional comments',
      conditions:
        vehicleData.hasAdditionalComments === Answer.Yes
          ? [{ icon: null, label: vehicleData.additionalComments, type: issueTypes.text }]
          : [{ icon: null, label: 'No additional comments provided', type: issueTypes.text }],
    },
  ]
}

const exteriorIssues = ['driverSideIssue', 'passengerSideIssue', 'frontSideIssue', 'rearSideIssue']
const interiorIssues = [
  'carpetOrUpholsteryPetHairIssue',
  'carpetOrUpholsteryIssue',
  'trimOrDashboardIssue',
  'petOdorIssue',
  'odorIssue',
]
const mechanicalIssues = [
  'engineHasIssues',
  'transmissionHasIssues',
  'serviceLightsOn',
  'milesDrivenOnCurrentTires',
  'airConditioning',
  'hasAdditionalComments',
]

const tabsOrder = {
  exteriorIssues: 0,
  interiorIssues: 1,
  mechanicalIssues: 2,
  tires: 3,
}

const normalizedTabsOrder = {
  [tabsOrder.exteriorIssues]: 'exteriorIssues',
  [tabsOrder.interiorIssues]: 'interiorIssues',
  [tabsOrder.mechanicalIssues]: 'mechanicalIssues',
  [tabsOrder.tires]: 'tires',
}

export default function Inspection() {
  const { vehicleData } = useVehiclePageData()

  const issueCount = useMemo(
    () => ({
      exteriorIssues: getIssueCount(exteriorIssues, vehicleData),
      interiorIssues: getIssueCount(interiorIssues, vehicleData),
      mechanicalIssues: getIssueCount(mechanicalIssues, vehicleData),
    }),
    [vehicleData],
  )

  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    let activeTab = tabsOrder.exteriorIssues

    Object.entries(issueCount).forEach(([key, value]) => {
      if (value > issueCount[normalizedTabsOrder[activeTab]]) activeTab = tabsOrder[key]
    })

    return activeTab
  })

  const tabs = useMemo(() => {
    const initialTabs = [
      { tab: 'Exterior', count: issueCount.exteriorIssues, visible: true },
      { tab: 'Interior', count: issueCount.interiorIssues, visible: true },
      { tab: 'Mechanical', count: issueCount.mechanicalIssues, visible: true },
      {
        tab: 'Tires',
        visible: vehicleData.vehicleType === VehicleType.Consumer,
      },
    ]

    return initialTabs.filter(({ visible }) => visible)
  }, [
    issueCount.exteriorIssues,
    issueCount.interiorIssues,
    issueCount.mechanicalIssues,
    vehicleData.vehicleType,
  ])

  return (
    <>
      <div className='inspectionContainerWrapper flex justifyCenter flexDirectionColumn'>
        <h2 className='inspectionHeading'>Inspection Details</h2>

        <div className='tabsWrapper'>
          <Tabs
            activeIndex={activeTabIndex}
            onChange={(newIndex) => setActiveTabIndex(newIndex)}
            tabs={tabs}
          />
        </div>

        {activeTabIndex === tabsOrder.exteriorIssues && (
          <Exterior
            issues={generateExteriorIssues(vehicleData)}
            issueCount={issueCount.exteriorIssues}
          />
        )}
        {activeTabIndex === tabsOrder.interiorIssues && (
          <Interior
            issues={generateInteriorIssues(vehicleData)}
            issueCount={issueCount.interiorIssues}
          />
        )}
        {activeTabIndex === tabsOrder.mechanicalIssues && (
          <Mechanical
            issues={generateMechanicalIssues(vehicleData)}
            issueCount={issueCount.mechanicalIssues}
          />
        )}
        {activeTabIndex === tabsOrder.tires && <Tires photos={vehicleData.tirePhotos} />}
      </div>

      <style global jsx>{`
        .inspectionContainerWrapper {
          .inspectionHeading {
            margin: 4rem 0;
            font-size: 3.2rem;
            font-weight: 700;
            line-height: 4.3rem;
          }

          .tabsWrapper {
            margin: 0 0 4.75rem;
          }

          .issueSectionWrapper {
            width: 100%;

            .line {
              width: 100%;
              padding: 3.2rem 0;
              border-bottom: 1px solid var(--secondary-text-color);

              &.line:last-child {
                border-bottom: 0;
              }

              .item {
                flex: 1;
              }
            }
          }
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .inspectionContainerWrapper {
            .inspectionHeading {
              margin: 3.3rem 0 2rem;
              line-height: 2.7rem;
              font-size: 2rem;
            }

            .tabsWrapper {
              margin: 0;
            }

            .issueSectionWrapper {
              .line {
                padding: 2.6rem 0;
              }
            }
          }
        }

        @media (max-width: 1024px) {
          .inspectionContainerWrapper {
            padding: 0 0 3.8rem;

            .inspectionHeading {
              margin: 2.1rem 0;
              font-size: 2rem;
              line-height: 2.7rem;
            }

            .tabsWrapper {
              margin: 0 0 2.5rem;
            }

            .issueSectionWrapper {
              .line {
                padding: 1.65rem 0 1.05rem;
              }
            }
          }
        }
      `}</style>
    </>
  )
}
